/* eslint-disable camelcase -- matches React Router's export name */
import * as React from 'react'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router'

type PromptProps = {
  when?: boolean
  message: string | (() => string)
}

function PromptDataRouter({ when, message }: PromptProps) {
  const resolvedMessage = typeof message === 'function' ? message() : message
  const active = when !== false
  const blocker = useBlocker(active)
  const blockerRef = React.useRef(blocker)
  blockerRef.current = blocker

  // Blocked while `when` is false: `proceed` (not `reset`) so post-save navigation is not trapped.
  React.useEffect(() => {
    if (blocker.state !== 'blocked') {
      return undefined
    }
    if (!active) {
      const timeoutId = window.setTimeout(() => {
        const latest = blockerRef.current
        if (latest.state === 'blocked') {
          latest.proceed()
        }
      }, 0)
      return () => window.clearTimeout(timeoutId)
    }
    // eslint-disable-next-line no-alert -- intentional v5 Prompt parity (same as RR unstable_usePrompt)
    const shouldProceed = window.confirm(resolvedMessage)
    if (!shouldProceed) {
      blocker.reset()
      return undefined
    }
    const timeoutId = window.setTimeout(() => {
      const latest = blockerRef.current
      if (latest.state === 'blocked') {
        latest.proceed()
      }
    }, 0)
    return () => window.clearTimeout(timeoutId)
  }, [blocker, active, resolvedMessage])
  return null
}

function PromptBeforeUnload({ when, message }: PromptProps) {
  const resolvedMessage = typeof message === 'function' ? message() : message
  const active = when !== false
  React.useEffect(() => {
    if (!active) {
      return undefined
    }
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = resolvedMessage
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [active, resolvedMessage])
  return null
}

/**
 * v5-style unsaved-changes prompt.
 *
 * Do not use `react-router-dom`'s `unstable_usePrompt` here: under Jest + CJS it is often
 * undefined (interop / circular load via the `react-router` shim).
 *
 * Data router (`RouterProvider` / `createMemoryRouter`): `useBlocker` + confirm. The Lister app root
 * uses `createBrowserRouter` + `RouterProvider`, so production SPA navigations hit this path. We
 * detect the data router via `UNSAFE_DataRouterContext` — React Router's context for "running under
 * a data router". The `UNSAFE_` prefix marks API stability (may change across minors), not security;
 * `useBlocker` throws if used outside a data router, so we must branch. See
 * https://reactrouter.com/en/main/hooks/use-blocker
 *
 * `MemoryRouter` / other non-data routers: `beforeunload` only when `when` is true (tab close /
 * full reload).
 */
export function Prompt(props: PromptProps) {
  const dataRouter = React.useContext(UNSAFE_DataRouterContext)
  if (dataRouter != null) {
    return <PromptDataRouter {...props} />
  }
  return <PromptBeforeUnload {...props} />
}
